/**
 * client.ts — the typed, thin wrappers over the REAL provider endpoints.
 * =====================================================================
 *
 * These are the ONLY API calls a screen (Web Component / data-action) may use.
 * Every wrapper:
 *   - is typed against `generated/schema.ts` (generated from the committed
 *     openapi.yaml — `npm run gen:client`), so a screen cannot reference a
 *     field/endpoint the contract does not define; a typo fails `tsc`/the build.
 *   - calls `http.request`, which carries the same-origin session cookie + CSRF
 *     and handles 401/403 → login. No screen ever calls fetch or builds a URL.
 *
 * The contract today (verified against openapi.yaml + main/api/views.py):
 *   GET  /api/v1/provider/work-orders/                          list (paginated)
 *   GET  /api/v1/provider/work-orders/{short_name}/             retrieve
 *   POST /api/v1/provider/work-orders/{short_name}/accept/      offered → scheduled
 *   POST /api/v1/provider/work-orders/{short_name}/decline/     offered → unassigned
 *   POST /api/v1/provider/work-orders/{short_name}/schedule/    confirm pickup
 *   POST /api/v1/provider/work-orders/{short_name}/onsite/      arrive on site
 *   POST /api/v1/provider/work-orders/{short_name}/complete-onsite/  → processing
 *   POST /api/v1/provider/work-orders/{short_name}/sow/         SOW weights + deliverables
 *   POST /api/v1/provider/work-orders/{short_name}/inventory/   inventory CSV (poll GET ?task=)
 *   POST /api/v1/provider/work-orders/{short_name}/fmv/         FMV CSV → 55% settlement (poll GET ?task=)
 *   POST /api/v1/provider/work-orders/{short_name}/finalize/    FMV Approval → Mango Reviewing (ADMIN; finalize = purchase)
 *   POST /api/v1/provider/work-orders/{short_name}/fmv-decline/ reprice OR walk away instead of purchasing (ADMIN)
 *   POST /api/v1/provider/work-orders/{short_name}/deliverable-upload/  upload one SOW deliverable file (Equipment Processing)
 *   POST /api/v1/provider/work-orders/{short_name}/save-draft/  persist un-submitted SOW-completion form state
 *   POST   /api/v1/provider/work-orders/{short_name}/photos/             upload one onsite photo (before|truck), multipart
 *   DELETE /api/v1/provider/work-orders/{short_name}/photos/{id}/        remove one onsite photo (→ 204)
 *
 * The lookup field is `short_name` (DRF `lookup_field = 'short_name'`).
 *
 * fmv_2 INVARIANT: the ProviderWorkOrder schema deliberately omits fmv_2/fmv/
 * credit/invoice. Because these wrappers are typed against that schema, the type
 * system makes `fmv_2` unreferenceable here — there is no field to read. The
 * provider submits the per-item FMV-1 assessment via the `fmv` CSV upload; the
 * Mango-internal second valuation is never modelled here.
 */
import type { components } from "./generated/schema";
import { request } from "./http";

/** The provider-facing work order. Source of truth: openapi.yaml ProviderWorkOrder. */
export type WorkOrder = components["schemas"]["ProviderWorkOrder"];
export type PaginatedWorkOrders = components["schemas"]["PaginatedProviderWorkOrderList"];
export type ProviderStatus = components["schemas"]["ProviderStatusEnum"];

/** The SOW-completion body: weights + named completed deliverables. */
export type SowSubmit = components["schemas"]["SowSubmitRequest"];
/** Async upload + FMV settlement shapes. */
export type UploadTask = components["schemas"]["ProviderUploadTask"];
export type FmvTask = components["schemas"]["ProviderFmvTask"];
/** The provider-facing 55% settlement summary (purchase price + purchased/retained counts). */
export type FmvSettlement = components["schemas"]["FmvSettlementResponse"];
/** The requesting provider's own profile (Account screen + sidebar). */
export type ProviderProfile = components["schemas"]["ProviderProfile"];
/** The FMV decline/reprice body (Q3): reprice with a counter_price, OR walk away
 *  (walk_away=true); `reason` is required either way. Typed from the contract so a
 *  screen can only send what the API defines. */
export type FmvDeclineBody = components["schemas"]["FmvDeclineRequest"];
/** The accept body: the Admin's typed-name sign-off (optional at the API; the
 *  quick-accept modal requires it client-side). */
export type AcceptBody = components["schemas"]["AcceptRequest"];
/** The save-draft body: the un-submitted SOW-completion form state ({draft: {...}}),
 *  stored on sow_draft so a provider can resume. Typed from the generated contract. */
export type SaveDraftBody = components["schemas"]["SowDraftRequest"];

const BASE = "/api/v1/provider/work-orders";

/** GET the requesting provider's own profile (company, role, certs, COI, location). */
export function getProviderProfile(): Promise<ProviderProfile> {
  return request<ProviderProfile>("/api/v1/provider/profile/");
}

/** List parameters the contract supports (DRF pagination + search/ordering). */
export interface ListParams {
  page?: number;
  limit?: number;
  search?: string;
  ordering?: string;
}

/**
 * accept / decline / schedule take no meaningful body in this slice — modelled
 * as an explicit empty object so callers stay future-proof.
 */
export type TransitionBody = Record<string, never>;

/**
 * onsite / complete-onsite accept the onsite-scope capture body (now in the
 * contract): staff_count, scope_notes, and the client sign-off. Typed against
 * the generated OnsiteCompleteRequest so the form fields can only send what the
 * contract defines.
 */
export type OnsiteBody = components["schemas"]["OnsiteCompleteRequest"];

/**
 * The complete-onsite body, plus the optional onsite-photo flag. `no_photos:true`
 * records that the provider deliberately took no before/packed-truck photos (the
 * "No photos taken" checkbox) so the required-photo step is documented, not
 * hard-gated. (`no_photos` is being added to OnsiteCompleteRequest in openapi.yaml
 * by the backend; until that regenerates into `generated/schema.ts` it is layered
 * on here so the wrapper stays type-clean.)
 */
export type OnsiteCompleteBody = Partial<OnsiteBody> & { no_photos?: boolean };

/**
 * An onsite photo on the work order (before-collection or packed-truck). The
 * `url` is a same-origin media URL (served through the same CloudFront the portal
 * rides) so it renders under the strict `img-src 'self'` CSP. Mirrors the
 * `ProviderWorkOrderPhoto` shape the backend is adding to openapi.yaml; defined
 * locally until that regenerates into `generated/schema.ts`.
 */
export interface WorkOrderPhoto {
  id: number;
  url: string;
  kind: "before" | "truck" | "facility";
}

/** GET the provider's own work orders (queryset-scoped server-side). */
export function listWorkOrders(params: ListParams = {}): Promise<PaginatedWorkOrders> {
  return request<PaginatedWorkOrders>(`${BASE}/`, {
    query: {
      page: params.page,
      limit: params.limit,
      search: params.search,
      ordering: params.ordering,
    },
  });
}

/** GET a single work order by its short_name. */
export function getWorkOrder(shortName: string): Promise<WorkOrder> {
  return request<WorkOrder>(`${BASE}/${encodeURIComponent(shortName)}/`);
}

/** POST accept: Offered → Scheduled (stamps accepted_at + the optional sign-off
 *  name — "Admin accepts w/ signature"; the quick-accept modal supplies it). */
export function acceptWorkOrder(shortName: string, body: AcceptBody = {}): Promise<WorkOrder> {
  return request<WorkOrder>(`${BASE}/${encodeURIComponent(shortName)}/accept/`, {
    method: "POST",
    json: body,
  });
}

/** POST decline: Offered → unassigned (returns to the routing pool). */
export function declineWorkOrder(shortName: string): Promise<{ detail: string }> {
  return request<{ detail: string }>(`${BASE}/${encodeURIComponent(shortName)}/decline/`, {
    method: "POST",
    json: {},
  });
}

/** POST schedule: confirm pickup (stamps pickup_scheduled_at; stays Scheduled). */
export function scheduleWorkOrder(shortName: string, body: TransitionBody = {}): Promise<WorkOrder> {
  return request<WorkOrder>(`${BASE}/${encodeURIComponent(shortName)}/schedule/`, {
    method: "POST",
    json: body,
  });
}

/** POST onsite: arrive on site (stamps onsite_arrived_at — the "Arrive On Site" tap, NOT the SLA start).
 *  Accepts the optional onsite-scope body (staff_count / scope_notes / client sign-off). */
export function onsiteWorkOrder(shortName: string, body: Partial<OnsiteBody> = {}): Promise<WorkOrder> {
  return request<WorkOrder>(`${BASE}/${encodeURIComponent(shortName)}/onsite/`, {
    method: "POST",
    json: body,
  });
}

/** POST complete-onsite: Onsite → Equipment Processing (stamps onsite_completed_at —
 *  the "Onsite Job Complete" submission = BR#4 SLA-clock start). Sends staff_count,
 *  scope_notes, and the client sign-off (name or client_signoff_unavailable). */
export function completeOnsite(shortName: string, body: OnsiteCompleteBody = {}): Promise<WorkOrder> {
  return request<WorkOrder>(`${BASE}/${encodeURIComponent(shortName)}/complete-onsite/`, {
    method: "POST",
    json: body,
  });
}

/** POST sow: record total/e-waste weights + mark named deliverables complete (stamps sow_submitted_at). */
export function submitSow(shortName: string, body: SowSubmit = {}): Promise<WorkOrder> {
  return request<WorkOrder>(`${BASE}/${encodeURIComponent(shortName)}/sow/`, {
    method: "POST",
    json: body,
  });
}

/**
 * POST inventory: upload an asset-inventory CSV (Equipment Processing). The CSV's
 * dropoff_id is resolved server-side from the work order. Returns a signed task id;
 * `pollInventory` polls GET ?task=<id> for the import result. The browser builds the
 * multipart body from a real <input type=file> via the action handler.
 */
export function uploadInventory(shortName: string, form: FormData): Promise<UploadTask> {
  return request<UploadTask>(`${BASE}/${encodeURIComponent(shortName)}/inventory/`, {
    method: "POST",
    form,
  });
}

/** GET inventory ?task=<id>: poll the import result for a prior inventory upload. */
export function pollInventory(shortName: string, task: string): Promise<UploadTask> {
  return request<UploadTask>(`${BASE}/${encodeURIComponent(shortName)}/inventory/`, {
    query: { task },
  });
}

/**
 * POST fmv: upload the provider's per-item FMV assessment CSV (writes fmv_1 only).
 * Stamps fmv_submitted_at, advances to FMV Approval, and returns the provider-facing
 * 55% settlement (lot purchase price = 0.55 × sum(fmv_1 ≥ 150); items < 150 retained free).
 */
export function submitFmv(shortName: string, form: FormData): Promise<FmvTask> {
  return request<FmvTask>(`${BASE}/${encodeURIComponent(shortName)}/fmv/`, {
    method: "POST",
    form,
  });
}

/** GET fmv ?task=<id>: poll the FMV import result + settlement for a prior FMV upload. */
export function pollFmv(shortName: string, task: string): Promise<FmvTask> {
  return request<FmvTask>(`${BASE}/${encodeURIComponent(shortName)}/fmv/`, {
    query: { task },
  });
}

/**
 * POST finalize: FMV Approval → Mango Reviewing (PROVIDER-ADMIN ONLY; Staff get 403).
 * Stamps finalized_at and returns the 55% settlement summary. No Stripe/payment here.
 */
export function finalizeWorkOrder(shortName: string): Promise<FmvSettlement> {
  return request<FmvSettlement>(`${BASE}/${encodeURIComponent(shortName)}/finalize/`, {
    method: "POST",
    json: {},
  });
}

/**
 * POST fmv-decline: the FMV Approval *alternative* to purchasing (Q3, Jordan
 * 2026-06-02). PROVIDER-ADMIN ONLY (Staff get 403). `walk_away=true` → outcome
 * 'decline' (prep for Mango-coordinated pickup); otherwise 'reprice' with a
 * `counter_price`. `reason` is required either way. Routes the work order to Mango
 * Reviewing flagged for ops and returns the updated work order. The downstream
 * counter-bid / buyer-network routing is Mango-side and never provider-facing.
 */
export function fmvDeclineWorkOrder(shortName: string, body: FmvDeclineBody): Promise<WorkOrder> {
  return request<WorkOrder>(`${BASE}/${encodeURIComponent(shortName)}/fmv-decline/`, {
    method: "POST",
    json: body,
  });
}

/**
 * POST deliverable-upload: upload a single SOW deliverable file during Equipment
 * Processing. The browser builds the multipart body (deliverable_type + file) from
 * a real <input type=file> via the action handler; the server attaches the file and
 * marks that WorkOrderDeliverable complete. Returns the updated work order.
 */
export function uploadDeliverable(shortName: string, form: FormData): Promise<WorkOrder> {
  return request<WorkOrder>(`${BASE}/${encodeURIComponent(shortName)}/deliverable-upload/`, {
    method: "POST",
    form,
  });
}

/**
 * POST save-draft: persist the un-submitted SOW-completion form state to sow_draft
 * so a provider can "Save draft" and resume. Not authoritative — the `sow`
 * submission is. Returns the updated work order.
 */
export function saveDraft(shortName: string, body: SaveDraftBody = {}): Promise<WorkOrder> {
  return request<WorkOrder>(`${BASE}/${encodeURIComponent(shortName)}/save-draft/`, {
    method: "POST",
    json: body,
  });
}

/**
 * POST photos: upload ONE onsite photo (the before-collection or packed-truck
 * capture from the "Onsite job complete" screen). Multipart { image, kind };
 * the browser sets the multipart boundary (we never set Content-Type). Returns
 * the persisted photo ({ id, url, kind }) so the screen can render its thumbnail.
 *
 * Called directly by <mango-onsite> from a real <input type=file> change — NOT a
 * data-action — because it operates on a single picked File, not a work-order
 * lifecycle transition.
 */
export function uploadWorkOrderPhoto(
  shortName: string,
  kind: WorkOrderPhoto["kind"],
  file: File,
): Promise<WorkOrderPhoto> {
  const form = new FormData();
  form.append("image", file, file.name);
  form.append("kind", kind);
  return request<WorkOrderPhoto>(`${BASE}/${encodeURIComponent(shortName)}/photos/`, {
    method: "POST",
    form,
  });
}

/**
 * DELETE photos/{id}: remove one previously-uploaded onsite photo (the ✕ on a
 * thumbnail). Resolves with no value on the 204. Called directly by
 * <mango-onsite>, like uploadWorkOrderPhoto.
 */
export function deleteWorkOrderPhoto(shortName: string, photoId: number): Promise<void> {
  return request<void>(`${BASE}/${encodeURIComponent(shortName)}/photos/${photoId}/`, {
    method: "DELETE",
  });
}

/**
 * The single registry every `data-action` maps to. `bind.ts` looks an action up
 * here by name; a `data-action="accept"` button can only fire something that
 * exists in this object. This is also what the contract-usage lint greps.
 *
 * Each entry is `(id, body) => Promise`. For simple transitions `body` is unused;
 * for the form-driven ones (onsite / complete-onsite / sow) the screen passes a
 * typed body, and for the file uploads (inventory / fmv) the screen passes a
 * FormData built from a real <input type=file>. `bind.ts` supports an optional
 * per-action body-builder so the same delegated, CSP-safe, keyboard-operable
 * `data-action` wiring drives all of them.
 */
export const actions = {
  accept: acceptWorkOrder,
  decline: declineWorkOrder,
  schedule: scheduleWorkOrder,
  onsite: onsiteWorkOrder,
  "complete-onsite": completeOnsite,
  sow: submitSow,
  inventory: uploadInventory,
  fmv: submitFmv,
  finalize: finalizeWorkOrder,
  "fmv-decline": fmvDeclineWorkOrder,
  "deliverable-upload": uploadDeliverable,
  "save-draft": saveDraft,
} as const;

export type ActionName = keyof typeof actions;
